import { restClient } from '@/Core'

import type { ITransportDashboard, ITransportDynamicsPoint } from '@/Ship/Contracts/ITransportDashboard'

/** Ендпойнт віджетів перевезень (`SPEC-027` §3.3). Власник — `ReportContainer` бека. */
export const TRANSPORT_DASHBOARD_URL = '/services/reports/transport_dashboard'

type Raw = Partial<Record<string, unknown>> | null | undefined

const toCount = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? Math.max(0, Math.trunc(value)) : 0)

const toMoney = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0)

const asObject = (value: unknown): Raw => (value !== null && typeof value === 'object' ? (value as Raw) : null)

const toDynamics = (value: unknown): ITransportDynamicsPoint[] | null =>
    Array.isArray(value)
        ? value.map((item: unknown) => {
              const point = asObject(item)

              return {
                  year: toCount(point?.['year']),
                  month: toCount(point?.['month']),
                  count: toCount(point?.['count']),
                  profit: toMoney(point?.['profit']),
              }
          })
        : null

/**
 * Чотири блоки перевезень одним запитом (В-01): свої (`userUuid = null`) або
 * обраного співробітника — за правом `dashboard_others`.
 *
 * `forceRefresh` — бо 30-секундний GET-кеш ядрового клієнта віддав би старі
 * числа і після події `counters`, і після повернення на сторінку. Блок, якого
 * бек не віддав (`null`), лишається `null` — сторінка його не малює.
 *
 * Помилку не ковтає: стан «—» і повідомлення показує сторінка.
 */
export const LoadTransportDashboardTask = async (userUuid: string | null): Promise<ITransportDashboard> => {
    const response = (await restClient.get(
        TRANSPORT_DASHBOARD_URL,
        userUuid ? { searchParams: { user: userUuid } } : {},
        { forceRefresh: true }
    )) as unknown as Raw

    const user = asObject(response?.['user'])
    const noOffers = asObject(response?.['noOffers'])
    const applications = asObject(response?.['applications'])
    const loadings = asObject(response?.['loadings'])

    return {
        user: {
            uuid: String(user?.['uuid'] ?? ''),
            fullName: typeof user?.['fullName'] === 'string' ? user['fullName'] : null,
        },
        noOffers: noOffers
            ? {
                  request: toCount(noOffers['request']),
                  requestTotal: toCount(noOffers['requestTotal']),
                  tender: toCount(noOffers['tender']),
                  tenderTotal: toCount(noOffers['tenderTotal']),
                  application: toCount(noOffers['application']),
              }
            : null,
        applications: {
            new: toCount(applications?.['new']),
            offersToday: toCount(applications?.['offersToday']),
            confirmedToday: toCount(applications?.['confirmedToday']),
        },
        loadings: {
            loadingToday: toCount(loadings?.['loadingToday']),
            loadingWeek: toCount(loadings?.['loadingWeek']),
            loadingMonth: toCount(loadings?.['loadingMonth']),
            unloadingMonth: toCount(loadings?.['unloadingMonth']),
            unloadingPlannedMonth: toCount(loadings?.['unloadingPlannedMonth']),
            expectedProfit: toMoney(loadings?.['expectedProfit']),
        },
        dynamics: toDynamics(response?.['dynamics']),
    }
}
